import { Button, Divider, Grid, Paper, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAllFundraiser } from 'src/shared/services/fundraiserService';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { ArrowForwardIos } from '@mui/icons-material';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_PAGE_INDEX, PageSize } from 'src/shared/models/enums/gridFilterConfig';
import { FundraiserSearchFilter } from '../models/fundraiserSearchFilter';
import { FundraiserResponse } from '../models/fundraiserResponse';
import { FundraiserChartLine } from '../components/fundraiserChartLine';
import { FundraiserSkeletons } from '../components/fundraiserSkeletons';

export const FundraiserOverviewPage = () => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const navigate = useNavigate();

  const searchFilter = useMemo<FundraiserSearchFilter>(
    () => ({
      sortCol: 'fundraiser_id',
      sortDir: 'desc',
      pageIndex: DEFAULT_PAGE_INDEX,
      pageSize: PageSize.Size100,
      filter: '',
    }),
    [],
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [fundraisers, setfundraisers] = useState<FundraiserResponse[]>();

  const navigateHandler = (id: number) => {
    navigate(`${RoutesEnum.FundraiserDetail}/${id}`);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAllFundraiser(searchFilter);
      if (isMounted()) {
        setfundraisers(result.data.results);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [isMounted, searchFilter]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [searchFilter, fetchData, isMounted]);

  return (
    <GridWrapper>
      <Grid item xs={12}>
        <MainContentHeader title={t('fundraiser:pageTitle')} />
      </Grid>
      <Grid container spacing={4} p={2}>
        {loading && <FundraiserSkeletons />}
        {fundraisers &&
          fundraisers.length > 0 &&
          fundraisers.map((fundraiser) => {
            return (
              <Grid item xs={12} sm={6} md={4} key={fundraiser.fundraiser_id} sx={{ display: 'flex' }}>
                <Paper sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                  <Grid container py={2} px={1.5} sx={{ flexGrow: 1 }}>
                    <Grid item xs={12}>
                      <Typography sx={{ fontWeight: 500 }}>{fundraiser.heading}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography sx={{ display: 'inline' }} variant='h4' fontWeight={(thm) => thm.typography.fontWeightBold}>
                        <AmountWithCurrency amount={fundraiser.totalAmount} decimals={0} showCurrencyInSecondaryStyle currencyVariant='h5' />
                      </Typography>
                    </Grid>
                    <Grid item xs={12} mt={2}>
                      <FundraiserChartLine
                        loading={loading}
                        chartData={fundraiser.paymentsSummaryLastDays.length ? fundraiser.paymentsSummaryLastDays : [{ date: 'N/A', value: 0 }]}
                      />
                    </Grid>
                    <Grid item xs={12} mt={1}>
                      <Divider />
                    </Grid>
                    <Grid item xs={12} mt={1}>
                      <Grid container justifyContent='space-between' alignItems='center'>
                        <Typography variant='body2' sx={{ fontWeight: 500, textTransform: 'uppercase' }} color='textSecondary'>
                          {t('fundraiser:averageDonationAmount')}
                        </Typography>
                        <Typography sx={{ display: 'inline' }} fontWeight={(thm) => thm.typography.fontWeightBold}>
                          <AmountWithCurrency amount={fundraiser.averageAmount} decimals={0} showCurrencyInSecondaryStyle currencyVariant='body1' />
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} mt={1}>
                      <Divider />
                    </Grid>
                    <Grid item xs={12} mt={1}>
                      <Grid container justifyContent='space-between' alignItems='center'>
                        <Typography variant='body2' sx={{ fontWeight: 500, textTransform: 'uppercase' }} color='textSecondary'>
                          {t('fundraiser:donations')}
                        </Typography>
                        <Typography sx={{ display: 'inline' }} fontWeight={(thm) => thm.typography.fontWeightBold}>
                          {fundraiser.totalDonations || 0}{' '}
                          <Typography color='textSecondary' component='span'>
                            {t('fundraiser:count')}
                          </Typography>
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} mt={1}>
                      <Divider />
                    </Grid>
                    <Grid item xs={12} mt={1}>
                      <Button
                        variant='text'
                        size='small'
                        onClick={() => navigateHandler(fundraiser.fundraiser_id)}
                        endIcon={<ArrowForwardIos fontSize='inherit' />}
                      >
                        {t('common:button.showMore')}
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            );
          })}
      </Grid>
    </GridWrapper>
  );
};
